import { useRef, useEffect, useState } from "react";

interface SearchInputProps {
  keyword: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchResults?: any[];
  showDropdown?: boolean;
  onResultClick?: (result: any) => void;
  onCloseDropdown?: () => void;
  results?: { name: string; type: string; center: [number, number]; path?: string }[];
}

export default function SearchInput({
  keyword,
  onChange,
  placeholder,
  searchResults = [],
  showDropdown = false,
  onResultClick,
  onCloseDropdown,
  results = []
}: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 处理点击外部关闭下拉框
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        onCloseDropdown?.();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onCloseDropdown]);

  return (
    <div className="relative">
      <div className="flex items-center bg-white border border-slate-200 rounded-lg overflow-hidden focus-within:border-slate-400 transition-colors h-8">
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          <i className="ri-search-line text-slate-400 text-sm" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={keyword}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => keyword && results.length > 0 && showDropdown}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-slate-800 placeholder-slate-400 text-xs py-2 outline-none pr-2"
        />
        {keyword ? (
          <button
            onClick={() => onChange("")}
            className="px-2 py-2 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xs" />
          </button>
        ) : (
          <div className="px-2 py-2" />
        )}
      </div>

      {/* Search dropdown */}
      {showDropdown && keyword && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden"
        >
          {results.length > 0 ? (
            results.map((r, idx) => (
              <button
                key={`${r.type}-${r.name}-${idx}`}
                onClick={() => onResultClick?.(r)}
                className="w-full flex items-start gap-2 px-3 py-2 hover:bg-slate-50 transition-colors cursor-pointer text-left border-b border-slate-100 last:border-0"
              >
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`ri-map-pin-line text-xs text-slate-500`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-800 text-xs font-medium truncate">{r.name}</div>
                  {r.path && (
                    <div className="text-slate-400 text-xs truncate mt-0.5">{r.path}</div>
                  )}
                </div>
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 flex-shrink-0 text-slate-500">
                  {r.type}
                </span>
              </button>
            ))
          ) : (
            <div className="px-3 py-4 text-center">
              <div className="flex flex-col items-center gap-1.5">
                <i className="ri-search-line text-slate-300 text-lg" />
                <span className="text-slate-400 text-xs">未找到与"<span className="text-slate-500">{keyword}</span>"相关的内容</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}